<template>
  <div>
    <div v-if="showResults">
      <QuizzResultComponent
        :questions="video.questions || []"
        :videoId="video.id"
        @next="completedVideo('next')"
        @back="failVideo()"
      />
    </div>

    <div v-else>
      <div class="card" v-if="!showQuestions">
        <div class="card-header">
          <h5 class="card-header-title">
              {{ video.name }}
          </h5>
        </div>
        <div class="card-body p-0">
          <PlayerComponent :video-id="getVideoID" @playing="onPlaying" @ready="isReady($event)" @end="end" @play="playTimer" @stop="stopTimer" :key="componentKey" @update="isReady($event)" />
        </div>
        <div class="card-body text-center" v-if="playerReady">
          <div class="d-flex flex-column justify-content-center">
            <div class="alert alert-info text-center mb-4 flex-column" role="alert" v-if="hasQuestions">
              <p>Vous devez regarder la vidéo entièrement avant de pouvoir répondre aux questions.</p>
              <p><small>En cas de problème, rafraîchissez la page. La vidéo et le chronomètre reprendront à la dernière sauvegarde automatique.</small></p>
            </div>
            <div class="alert alert-info text-center mb-4" role="alert" v-else>
              Vous devez regarder la vidéo entièrement avant de regarder la prochaine vidéo.
            </div>
          </div>
          <div class="btn-group btn-group-lg" role="group" aria-label="Basic example">
            <button v-if="showQuestionsButton" type="button" class="btn btn-white" @click="showQuestions = true"><span class="text-dark">J'affiche les questions</span></button>
            <button v-else type="button" class="btn btn-white" disabled><span class="text-dark">{{ formattedTimeLeft }}</span></button>
          </div>
          <div class="d-flex justify-content-center mt-4">
            <button class="btn btn-outline-dark btn-sm" @click="resetVideo()">Recommencer la vidéo</button>
          </div>
          <div v-if="admin && hasQuestions">
            <p class="text-muted mt-4">En tant qu'administrateur, vous pouvez directement afficher les questions.</p>
            <p><a href="#" @click="adminPass">J'affiche les questions</a></p>
          </div>
          <div class="mt-4" v-if="totalQuestions === 0">
            <button type="button" class="btn btn-primary" @click="completedVideo('button')"><span class="text-dark">Prochaine vidéo</span></button>
          </div>
        </div>
      </div>
      <AnswerQuizzComponent v-else :question="currentQuestion" :totalQuestions="totalQuestions" @next="handleNextQuestion($event)" />
    </div>
  </div>
</template>

<script>
import AnswerQuizzComponent from './AnswerQuizzComponent.vue'
import PlayerComponent from './PlayerComponent.vue'
import getIdFromUrl from 'get-youtube-id'
import QuizzResultComponent from './QuizzResultComponent.vue'

export default {
  components: {
    AnswerQuizzComponent,
    QuizzResultComponent,
    PlayerComponent
  },

  data() {
    return {
      componentKey: 0,
      player: null,
      playerReady: false,
      showQuestions: false,
      videoTime: 0,
      isPlayTimer: true,
      currentQuestion: {},
      showQuestionsButton: false,
      backup: 0,
      lastSave: 0,
      showResults: false,
      timeout: null
    }
  },

  props: {
    videoId: Number,
    video: Object,
    questionsCompleted: Array,
    admin: Number,
    hasQuestions: Boolean
  },

  watch: {
    videoId() {
      this.handleCurrentQuestion(this.questionsCompleted)

      this.playerReady = false

      this.componentKey = this.componentKey + 1
    }
  },

  computed: {
    totalQuestions() {
      if (this.hasQuestions) {
        return this.video.questions.length
      }

      return 0
    },

    getVideoID () {
      return getIdFromUrl(this.video.video)
    },

    formattedTimeLeft() {
      const timeLeft = this.videoTime
      const minutes = Math.floor(timeLeft / 60)

      let seconds = (timeLeft % 60).toFixed(0)
      if (seconds < 10) {
        seconds = `0${seconds}`
      }

      // The output in MM:SS format
      return `${minutes}:${seconds}`
    },
  },

  methods: {
    resetVideo() {
      this.resetBackup()

      const playerTime = this.player.duration
      this.videoTime = playerTime
      this.player.currentTime = 0
    },

    onPlaying() {
      this.videoTime = this.player.duration - this.player.currentTime
      this.saveBackup()
    },

    end() {
      if (this.hasQuestions) {
        this.showQuestionsButton = true
      } else {
        this.completedVideo()
      }

      this.isPlayTimer = false
    },

    stopTimer() {
      this.isPlayTimer = false
    },

    playTimer() {
      this.isPlayTimer = true
    },

    findQuestionByStep(step) {
      return this.video.questions.find(q => q.step === step);
    },

    handleNextQuestion(question) {
      if (this.hasQuestions) {
        const nextQuestion = this.findQuestionByStep(question.step + 1)

        this.showQuestionsButton = false

        if (nextQuestion) {
          this.currentQuestion = nextQuestion
        } else {
          this.showResults = true
        }
      }
    },

    handleCurrentQuestion(questionsCompleted) {
      if (this.hasQuestions) {
        if (questionsCompleted.length === 0) {
          this.currentQuestion = this.findQuestionByStep(1)
        } else {
          const maxStep = Math.max(...this.questionsCompleted.map(q => q.question_step))

          const nextQuestion = this.findQuestionByStep(maxStep + 1)

          if (nextQuestion) {
            this.showQuestions = true
            this.currentQuestion = nextQuestion
          } else {
            this.showResults = true
          }
        }
      }
    },

    completedVideo() {
      axios.post(`/webapi/videos/${this.video.id}/complete`)
        .then(() => {
          this.resetComponent()
        })
        .catch(err => console.log(err))
    },

    failVideo() {
      axios.get(`/webapi/videos/${this.video.id}/fail`)
        .then(() => {
          this.showQuestions = false
          this.showResults = false
          this.getVideoTime()
          this.currentQuestion = this.findQuestionByStep(1)
          this.resetBackup()
        })
        .catch(err => console.log(err))
    },

    fetchBackup() {
      if (localStorage) {
        const videoBackup = localStorage.getItem('video_backup')

        if (videoBackup) {
          const parsedVideoSave = JSON.parse(videoBackup)

          if (parsedVideoSave[this.video.course_id]) {
            if (parsedVideoSave[this.video.course_id][this.video.id]) {
              const backupTimer = parseInt(parsedVideoSave[this.video.course_id][this.video.id], 10)

              this.player.currentTime = backupTimer
              this.videoTime = this.player.duration - backupTimer
            }
          }
        }
      }
    },

    resetComponent() {
      this.showQuestions = false
      this.showResults = false
      this.getVideoTime()
      this.$emit('nextvideo')
    },

    getVideoTime() {
      if (this.player) {
        const playerTime = this.player.duration
        this.videoTime = playerTime
      }
    },

    saveBackup() {
      if (localStorage && this.player) {
        const videoBackup = localStorage.getItem('video_backup')

        const timer = this.player.currentTime.toFixed(0)

        if (videoBackup) {
          const parsedVideoSave = JSON.parse(videoBackup)

          if (parsedVideoSave[this.video.course_id]) {
            parsedVideoSave[this.video.course_id][this.video.id] = timer
          } else {
            parsedVideoSave[this.video.course_id] = { [this.video.id]: timeToSave }
          }

          localStorage.setItem('video_backup', JSON.stringify(parsedVideoSave))
        } else {
          localStorage.setItem('video_backup', JSON.stringify({ [this.video.course_id]: { [this.video.id]: timer } }))
        }
      }
    },

    resetBackup () {
      if (localStorage && this.player) {
        const videoBackup = localStorage.getItem('video_backup')

        if (videoBackup) {
          const parsedVideoSave = JSON.parse(videoBackup)

          if (parsedVideoSave[this.video.course_id]) {
            if (parsedVideoSave[this.video.course_id][this.video.id]) {
              delete parsedVideoSave[this.video.course_id][this.video.id]

              localStorage.setItem('video_backup', JSON.stringify(parsedVideoSave))
            }
          }
        }
      }
    },

    isReady(player) {
      this.player = player
      this.playerReady = true

      this.getVideoTime()

      if (this.video.id) {
        this.fetchBackup()
      }
    },

    adminPass() {
      this.showQuestions = true
    },
  },

  mounted() {
    this.handleCurrentQuestion(this.questionsCompleted)
  },
}
</script>
